/**
 * Formie Error Handler
 * Detects CSRF token mismatches (400 errors) on Formie forms
 * and displays user-friendly messages
 */

import { showError, CSRF_ERROR_MESSAGE } from '../lib/csrf-error-handler.js';

/**
 * Check if an alert element contains a CSRF/session error
 * @param {Element} alertElement - The Formie alert element
 * @returns {boolean}
 */
function isCsrfErrorAlert(alertElement) {
    const text = alertElement.textContent || alertElement.innerText || '';
    const lowerText = text.toLowerCase();
    
    // Check for actual CSRF error messages, not just any 400 error
    const csrfErrorIndicators = [
        'unable to verify your data submission',
        'csrf',
        'verify your data',
        'data submission',
        'session has expired'
    ];
    
    const has400Status = text.trim().startsWith('400');
    const hasCsrfMessage = csrfErrorIndicators.some(indicator => 
        lowerText.includes(indicator)
    );
    
    return has400Status || hasCsrfMessage;
}

/**
 * Replace Formie's error alert with our custom CSRF message
 * @param {Element} alertElement - The Formie alert element to modify
 */
function replaceCsrfErrorMessage(alertElement) {
    // Replace the alert content with our friendly message
    alertElement.innerHTML = `<p>${CSRF_ERROR_MESSAGE}</p>`;

    // Also show in the notification bar at top of page
    showError(CSRF_ERROR_MESSAGE, true);
}

/**
 * Handle mutations to detect when Formie adds error alerts
 * @param {MutationRecord[]} mutations - The mutation records
 */
function handleMutations(mutations) {
    for (const mutation of mutations) {
        // Check added nodes
        for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
                // Check if this is a Formie alert
                if (node.matches && node.matches('[data-fui-alert]')) {
                    if (isCsrfErrorAlert(node)) {
                        replaceCsrfErrorMessage(node);
                    }
                }

                // Also check children (in case alert is nested in added content)
                const alerts = node.querySelectorAll ? node.querySelectorAll('[data-fui-alert]') : [];
                alerts.forEach(alert => {
                    if (isCsrfErrorAlert(alert)) {
                        replaceCsrfErrorMessage(alert);
                    }
                });
            }
        }

        // Also check for attribute/content changes on existing alerts
        if (mutation.type === 'characterData' || mutation.type === 'childList') {
            const target = mutation.target;
            const alertElement = target.closest ? target.closest('[data-fui-alert]') : null;

            if (alertElement && isCsrfErrorAlert(alertElement)) {
                replaceCsrfErrorMessage(alertElement);
            }
        }
    }
}

/**
 * Check existing alerts on the page for CSRF errors
 */
function checkExistingAlerts() {
    const alerts = document.querySelectorAll('[data-fui-alert]');
    alerts.forEach(alert => {
        if (isCsrfErrorAlert(alert)) {
            replaceCsrfErrorMessage(alert);
        }
    });
}

/**
 * Initialize Formie error handling
 * Uses MutationObserver to detect when Formie adds error alerts
 */
function initFormieErrorHandling() {
    // Check any existing alerts on page load
    checkExistingAlerts();

    // Set up MutationObserver to watch for new alerts
    const observer = new MutationObserver(handleMutations);

    // Observe the entire document for added nodes
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true
    });
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFormieErrorHandling);
} else {
    initFormieErrorHandling();
}

export { initFormieErrorHandling };
